import { useState, useRef, useEffect, useCallback } from "react";
import { axiosInstance } from "../../config";
import "../AgencyOnboarding/AgencyOnboarding.css";
import "./ClipperApply.css";

/**
 * ClipperApplyPage — Typeform-style clipper/editor recruitment. Screens (never
 * rejects) and routes everyone to the Whop campaign. Answers post to
 * /api/clipper-apply. Reuses the agency-onboarding "agon-" styles.
 */
const WHOP_URL = "https://whop.com/clipbait-ai";

const STEPS = [
  { id: "intro", type: "welcome" },
  { id: "email", type: "email", key: "email", n: 1, required: true, title: "What's your email?", hint: "So we can reach you about campaigns." },
  {
    id: "experience", type: "single", key: "experience", n: 2, required: true,
    title: "Do you have experience creating shorts for personal brands?",
    options: [{ key: "Yes", label: "Yes" }, { key: "No", label: "No" }],
  },
  { id: "portfolio", type: "shorttext", key: "portfolio", n: 3, required: true, title: "Link to some of your recent short-form edits", hint: "Drive, YouTube, IG, a portfolio — whatever shows your work." },
  {
    id: "software", type: "single", key: "software", n: 4, required: true,
    title: "What editing software do you primarily use?",
    options: [{ key: "Adobe Premiere", label: "Adobe Premiere" }, { key: "Capcut", label: "Capcut" }, { key: "Other", label: "Other" }],
  },
];
const TOTAL = STEPS.length;

export default function ClipperApplyPage() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({ email: "", experience: "", portfolio: "", software: "", softwareOther: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const step = STEPS[index];
  const isLast = index === TOTAL - 1;

  useEffect(() => {
    setError("");
    const t = setTimeout(() => inputRef.current?.focus(), 260);
    return () => clearTimeout(t);
  }, [index]);

  const set = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

  const isFilled = useCallback((s) => {
    if (s.type === "welcome") return true;
    if (s.type === "email") return /^\S+@\S+\.\S+$/.test(answers.email.trim());
    if (s.type === "single") {
      if (!answers[s.key]) return false;
      if (s.key === "software" && answers.software === "Other") return answers.softwareOther.trim().length > 0;
      return true;
    }
    return answers[s.key]?.trim().length > 0;
  }, [answers]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await axiosInstance.post("/clipper-apply", answers);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error === "email_required" ? "Please enter a valid email." : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [answers, submitting]);

  const next = useCallback(() => {
    if (step.required && !isFilled(step)) { setError("This one's required to continue."); return; }
    if (isLast) { submit(); return; }
    setDir(1);
    setIndex((i) => Math.min(TOTAL - 1, i + 1));
  }, [step, isLast, isFilled, submit]);

  const back = useCallback(() => {
    if (index === 0) return;
    setDir(-1);
    setIndex((i) => Math.max(0, i - 1));
  }, [index]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); next(); }
  };

  const pick = (key, optKey) => {
    setError("");
    set(key, optKey);
  };

  // ── Accepted screen → Whop ────────────────────────────────────────────────
  if (done) {
    return (
      <div className="agon">
        <div className="agon-stage">
          <div className="agon-screen agon-done">
            <div className="agon-check">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h1 className="agon-q">You're in 🎉</h1>
            <p className="agon-sub">You've been accepted to the Clipbait editing team. Here's how to start earning:</p>
            <ol className="cla-steps">
              <li>Join our Whop community below.</li>
              <li>Open the <b>Active Campaigns</b> tab.</li>
              <li>Start clipping — get paid per campaign, uncapped.</li>
            </ol>
            <a className="cla-cta" href={WHOP_URL} target="_blank" rel="noreferrer">Join the Whop &amp; start earning →</a>
            <p className="agon-sub cla-support">Any questions? DM the support chat inside the Whop.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agon">
      <div className="agon-progress"><div className="agon-progress-fill" style={{ width: `${(index / (TOTAL - 1)) * 100}%` }} /></div>

      <div className="agon-stage">
        <div key={step.id} className={`agon-screen ${dir >= 0 ? "agon-in-fwd" : "agon-in-back"}`}>
          {step.type !== "welcome" && (
            <div className="agon-num">{step.n}<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 12 19 12" /><polyline points="12 5 19 12 12 19" /></svg></div>
          )}

          {/* WELCOME — the pitch */}
          {step.type === "welcome" && (
            <>
              <div className="cla-eyebrow">CLIPBAIT · VIRAL SHORTS EDITOR</div>
              <h1 className="agon-q">Get hired to clip for our creators</h1>
              <p className="agon-sub">An exclusive spot on the Clipbait editing team. Produce 1–2 shorts a day for campaigns.</p>
              <div className="cla-perks">
                <div className="cla-perk"><b>Uncapped CPM</b><span>Get paid per view, no ceiling</span></div>
                <div className="cla-perk"><b>$1K+/mo potential</b><span>Based on your performance</span></div>
                <div className="cla-perk"><b>1–2 shorts / day</b><span>Steady, ongoing work</span></div>
              </div>
            </>
          )}

          {/* EMAIL */}
          {step.type === "email" && (
            <>
              <h1 className="agon-q">{step.title}<span className="agon-star">*</span></h1>
              {step.hint && <p className="agon-hint">{step.hint}</p>}
              <input ref={inputRef} type="email" className="agon-input" placeholder="you@email.com"
                value={answers.email} onChange={(e) => set("email", e.target.value)} onKeyDown={onKeyDown} />
            </>
          )}

          {/* SHORT TEXT */}
          {step.type === "shorttext" && (
            <>
              <h1 className="agon-q">{step.title}<span className="agon-star">*</span></h1>
              {step.hint && <p className="agon-hint">{step.hint}</p>}
              <input ref={inputRef} type="text" className="agon-input" placeholder="Paste a link…"
                value={answers[step.key]} onChange={(e) => set(step.key, e.target.value)} onKeyDown={onKeyDown} />
            </>
          )}

          {/* SINGLE SELECT */}
          {step.type === "single" && (
            <>
              <h1 className="agon-q">{step.title}<span className="agon-star">*</span></h1>
              <div className="agon-options">
                {step.options.map((o, i) => {
                  const on = answers[step.key] === o.key;
                  return (
                    <button key={o.key} ref={i === 0 ? inputRef : null}
                      className={`agon-opt ${on ? "on" : ""}`} onClick={() => pick(step.key, o.key)}>
                      <span className="agon-opt-key">{String.fromCharCode(65 + i)}</span>
                      <span className="agon-opt-label">{o.label}</span>
                      {on && <svg className="agon-opt-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                    </button>
                  );
                })}
              </div>
              {step.key === "software" && answers.software === "Other" && (
                <input className="agon-input cla-other" placeholder="Which software?"
                  value={answers.softwareOther} onChange={(e) => set("softwareOther", e.target.value)} onKeyDown={onKeyDown} />
              )}
            </>
          )}

          {error && <p className="agon-error">{error}</p>}

          <div className="agon-foot">
            <button className="agon-ok" onClick={next} disabled={submitting}>
              {submitting ? "Submitting…" : isLast ? "Submit" : step.type === "welcome" ? "Apply now" : "OK"}
              {!submitting && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
            </button>
            <span className="agon-enter">press Enter ↵</span>
          </div>
        </div>
      </div>

      <div className="agon-nav">
        <button className="agon-arrow" onClick={back} disabled={index === 0} aria-label="Previous">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
        </button>
        <button className="agon-arrow" onClick={next} disabled={submitting} aria-label="Next">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </div>
    </div>
  );
}
